import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCreativeJobsMap, upsertJob } from '@/store/slices/creativeJobs';
import { ATF_CARDS_DRAWS, ATF_CARDS_KIND, ATF_CARDS_PLACEMENT, regenerateCards } from './above-the-fold-api';
import { activeAtfCardsRun, settledAtfCardsRuns, type AtfCardsRun } from './atf-cards-run';

export interface AtfCardsRunState {
  /** The run in flight, if any (survives a reload: it is read from the jobs feed). */
  run: AtfCardsRun | null;
  /** True from the click until the service has answered the queue request. */
  queueing: boolean;
  regenerate: () => void;
}

/**
 * Regenerate the brand's Casino and Sports cards: queue the paid job, follow it through the jobs feed (the websocket
 * and the polled list), and when it ends re-read the cards (`onDone`) or say why it failed (`onError`). A 402 opens
 * Buy credits with what is missing (`onNeedCredits`).
 */
export const useAtfCardsRun = (opts: {
  tenantId: number | undefined;
  domain: string;
  recordId: string | undefined;
  onDone: () => void;
  onError: (message: string) => void;
  onNeedCredits: (needed?: number) => void;
}): AtfCardsRunState => {
  const { tenantId, domain, recordId, onDone, onError, onNeedCredits } = opts;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const jobs = useAppSelector(selectCreativeJobsMap);
  const [queueing, setQueueing] = React.useState(false);
  const run = activeAtfCardsRun(jobs, tenantId);

  // The runs this page has seen in flight, so each one's end is acted on once. A run found in the feed counts too:
  // one queued before a reload still ends with the page re-reading the cards.
  const followed = React.useRef(new Set<string>());
  // The callbacks change identity with the page's renders; the effect below reads the latest through a ref so a
  // settled run is acted on exactly once, whichever render sees it.
  const handlers = React.useRef({ onDone, onError });
  React.useEffect(() => {
    handlers.current = { onDone, onError };
  }, [onDone, onError]);

  // A brand switch stops following the last brand's runs: their end is not news on this brand's page.
  React.useEffect(() => {
    followed.current.clear();
  }, [tenantId]);

  const runId = run?.jobId;
  React.useEffect(() => {
    // Syncs with the jobs feed (websocket and polled list): follows the brand's run in flight, and acts once on each
    // followed run that has ended.
    if (runId) followed.current.add(runId);
    for (const end of settledAtfCardsRuns(jobs, followed.current)) {
      followed.current.delete(end.jobId);
      if (end.done) handlers.current.onDone();
      // The job's error is the service's own wording (English, never for the owner): the owner reads this.
      else handlers.current.onError(t("The cards couldn't be regenerated. Your credits were refunded."));
    }
  }, [jobs, runId, t]);

  const regenerate = React.useCallback((): void => {
    if (queueing || run || tenantId === undefined || !recordId || !domain) return;
    setQueueing(true);
    void regenerateCards(tenantId, domain, recordId)
      .then((result) => {
        if (result.ok) {
          followed.current.add(result.jobId);
          dispatch(
            upsertJob({
              id: result.jobId,
              tenant_id: String(tenantId),
              root_banner_id: recordId,
              kind: ATF_CARDS_KIND,
              placement: ATF_CARDS_PLACEMENT,
              status: result.status,
              completed: 0,
              total: ATF_CARDS_DRAWS,
            }),
          );
        } else if (result.status === 402) {
          onNeedCredits(result.needed);
        } else {
          onError(t("The cards couldn't be regenerated. Please try again."));
        }
      })
      .finally(() => setQueueing(false));
  }, [queueing, run, tenantId, recordId, domain, dispatch, onNeedCredits, onError, t]);

  return { run, queueing, regenerate };
};
